import { MapsApiOpts, MapEvents } from '../models/google-map'

export class MapsApiLoader {
	private static isLoading = false

	private static get isLoaded() {
		return typeof google === 'object' && typeof google.maps === 'object'
	}

	private static getScriptSource(loaderOpts: MapsApiOpts) {
		let source = loaderOpts.url ? loaderOpts.url : 'https://maps.googleapis.com/maps/api/js'
		const language = loaderOpts.language ? `&language=${loaderOpts.language}` : ''
		const region = loaderOpts.region ? `&region=${loaderOpts.region}` : ''
		const channel = loaderOpts.channel ? `&channel=${loaderOpts.channel}` : ''
		source += `?v=weekly&key=${loaderOpts.key}` + channel

		return `${source}${language}${region}&libraries=places`
	}

	/**
	 * Returns a promise that loads the Google Maps API.
	 * If the API is already loaded then resolves immediately.
	 * If the API is currently loading then resolves when finished.
	 * @param opts Options to load the API with. These ***cannot*** be changed once the API has been loaded on a page.
	 */
	public static load(opts: MapsApiOpts): Promise<void> {
		if (MapsApiLoader.isLoaded) return new Promise<void>(resolve => resolve())

		/* eslint-disable prettier/prettier*/
		if (MapsApiLoader.isLoading) return new Promise<void>((resolve, reject) => {
			window.addEventListener(MapEvents.loaded, () => resolve(), { once: true })
			window.addEventListener(MapEvents.error, (e: CustomEvent) => reject(e.detail), {once: true })
		})
		/* eslint-enable prettier/prettier*/

		MapsApiLoader.isLoading = true

		return new Promise<void>((resolve, reject) => {
			const script = document.createElement('script')
			script.type = 'text/javascript'
			script.src = MapsApiLoader.getScriptSource(opts)
			script.onerror = () => {
				MapsApiLoader.isLoading = false
				const error = new Error('Google Maps API failed to load.')
				reject(error)
				window.dispatchEvent(new CustomEvent(MapEvents.error, { detail: error }))
			}
			script.onload = () => {
				MapsApiLoader.isLoading = false
				resolve()
				window.dispatchEvent(new CustomEvent(MapEvents.loaded))
			}

			document.body.appendChild(script)
		})
	}
}
